import { API_URL } from './api';
import { getOfflineAccountScope, isOfflineScopeCurrent, scopedOfflineKey } from './scopedOfflineQueue';

export const getTerminalScope = getOfflineAccountScope;
export const getTerminalStorageKey = (base = 'berestoTerminalReader', scope = getTerminalScope()) => scopedOfflineKey(base, scope);
const pendingKey = scope => getTerminalStorageKey('berestoTerminalPayment', scope);
const assertScope = scope => {
  if (!isOfflineScopeCurrent(scope)) throw new Error('Compte modifié : rechargez avant de reprendre le paiement.');
};
export function readPendingTerminalPayment(scope = getTerminalScope()) {
  if (!scope) return null;
  try {
    const value = JSON.parse(localStorage.getItem(pendingKey(scope)) || 'null');
    return value?.scope === scope && Number.isSafeInteger(value.orderId) && value.orderId > 0 ? value : null;
  } catch { return null; }
}
export function persistPendingTerminalPayment(value, scope = getTerminalScope()) {
  assertScope(scope);
  const previous = readPendingTerminalPayment(scope);
  const orderId = Number(value.orderId);
  if (!Number.isSafeInteger(orderId) || orderId <= 0) throw new Error('Commande invalide.');
  if (previous && previous.orderId !== orderId) throw new Error('Reprenez le paiement précédent avant un nouvel encaissement.');
  const allowed = ['state', 'readerId', 'amountCents', 'provider'];
  const next = { orderId, scope };
  for (const key of allowed) if (value[key] !== undefined || previous?.[key] !== undefined) next[key] = value[key] ?? previous[key];
  const context = value.context || previous?.context ? { ...previous?.context, ...value.context } : null;
  if (context) {
    next.context = {};
    for (const key of ['source', 'ticketNumber', 'cart', 'total', 'printOptions', 'printState', 'printProductionTickets']) if (context[key] !== undefined) next.context[key] = context[key];
  }
  // Provider responses can contain a PaymentIntent client secret. Never store them.
  const receipt = value.receipt || previous?.receipt;
  if (receipt) {
    next.receipt = {};
    for (const key of ['provider', 'status', 'brand', 'last4', 'network', 'authorizationCode', 'receiptUrl', 'paymentIntentId', 'chargeId', 'amountCents', 'currency']) if (receipt[key] !== undefined) next.receipt[key] = receipt[key];
    if (receipt.receipt && typeof receipt.receipt === 'object') {
      next.receipt.receipt = {};
      for (const key of ['authorization_code', 'application_preferred_name', 'dedicated_file_name', 'transaction_status_information', 'application_cryptogram', 'terminal_verification_results', 'transaction_trace_number', 'siret_number', 'bank_code', 'acceptor_identifier', 'acceptance_system_terminal_application_identifier', 'card_acceptor_logical_number', 'system_acceptor_logical_number']) {
        if (typeof receipt.receipt[key] === 'string') next.receipt.receipt[key] = receipt.receipt[key].slice(0, 200);
      }
    }
  }
  localStorage.setItem(pendingKey(scope), JSON.stringify(next));
  return next;
}
export function clearPendingTerminalPayment(orderId, scope = getTerminalScope()) {
  assertScope(scope);
  if (readPendingTerminalPayment(scope)?.orderId === Number(orderId)) localStorage.removeItem(pendingKey(scope));
}
export function readTerminalReader(scope = getTerminalScope()) {
  try { return localStorage.getItem(getTerminalStorageKey('berestoTerminalReader', scope)) || ''; } catch { return ''; }
}
export const readPreferredTerminalReader = readTerminalReader;
export function saveTerminalReader(readerId, scope = getTerminalScope()) {
  assertScope(scope);
  localStorage.setItem(getTerminalStorageKey('berestoTerminalReader', scope), String(readerId || ''));
}
async function request(fetchWithAuth, path, options, scope) {
  assertScope(scope);
  const response = await fetchWithAuth(`${API_URL}${path}`, { cache: 'no-store', ...options });
  assertScope(scope);
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw Object.assign(new Error(payload?.error || 'Terminal indisponible. Vérifiez le paiement avant toute autre tentative.'), { status: response.status });
  return payload;
}
export async function getTerminalConfig(fetchWithAuth, scope = getTerminalScope()) {
  const config = await request(fetchWithAuth, '/api/payments/terminal/config', {}, scope);
  if (typeof config?.enabled !== 'boolean' || (config.enabled && !Array.isArray(config.readers))) throw new Error('Configuration du terminal indisponible. Aucun paiement lancé.');
  return config;
}
export async function terminalPaymentRequest(fetchWithAuth, orderId, action, body = {}, scope = getTerminalScope()) {
  if (!['start', 'status', 'cancel', 'retry'].includes(action)) throw new Error('Action terminal invalide.');
  const id = Number(orderId);
  if (!Number.isSafeInteger(id) || id <= 0) throw new Error('Commande invalide.');
  if (action !== 'status') persistPendingTerminalPayment({ orderId: id, state: 'uncertain', ...(['start', 'retry'].includes(action) ? { readerId: body.readerId } : {}) }, scope);
  const payload = await request(fetchWithAuth, `/api/orders/${id}/terminal/${action}`, action === 'status' ? {} : {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  }, scope);
  if (Number(payload?.orderId) !== id || payload?.provider !== 'stripe_terminal' || !['pending', 'paid', 'declined', 'cancelled', 'uncertain'].includes(payload?.state)) throw new Error('Réponse terminal incohérente : paiement à vérifier.');
  persistPendingTerminalPayment(payload, scope);
  return payload;
}

export function paymentIncludesCard(method, splits = []) {
  return method === 'cb' || (method === 'mixte' && splits.some(row => row.method === 'cb'));
}

export async function getTerminalReceipt(fetchWithAuth, orderId, scope = getTerminalScope()) {
  const id = Number(orderId);
  if (!Number.isSafeInteger(id) || id <= 0) throw new Error('Commande invalide.');
  const result = await request(fetchWithAuth, `/api/orders/${id}/terminal/status`, {}, scope);
  if (Number(result?.orderId) !== id || result.provider !== 'stripe_terminal' || result.state !== 'paid' || result.receipt?.status !== 'succeeded') throw new Error('Reçu carte confirmé indisponible. Le paiement doit être vérifié.');
  return result.receipt;
}
